import { useEffect, useState, useMemo, useCallback } from "react";
import CustomCard from "../../components/CustomCard/CustomCard";
import { useSelector, useDispatch } from "react-redux";
import useSearchableTable from "../../hooks/useSearchableTable";
import useUserPrivileges from "../../hooks/useUserPrivileges";
import useDocDownloadError from "../../hooks/useDocDownloadError";
import TableHeader from "../../components/CustomTable/TableHeader";
import DealsTable from "./Components/DealsTable";
import { capitalizeEntireWord, downloadPageHandle, generateStatusTableTitle } from "../../helpers/helpers";
import { DEAL_STATUS_LABEL_MAP, DEAL_STATUS } from "../../constants/constants";
import "./Deals.scss";
import { dealsView, count } from "../../redux/slices/dealsSlice";
import { getStatusList } from "../../redux/slices/EnqMngtSlice";
import { DEAL_STATUS_PRIVILEGE_MAP } from "../../constants/PermissionCodes";

const IS_DEAL_STATUS_IDS = Object.values(DEAL_STATUS).map((s) => s.ID);
const DEFAULT_DEAL_CATEGORY_ID = "DCT1000001";
const DEFAULT_DEAL_CATEGORY_NAME = "Invoice Financing";

const DEFAULT_DEAL_STATUSES = [
  DEAL_STATUS.PENDING.ID,
  DEAL_STATUS.APPROVED.ID,
];

const buildStatusTableTitle = ({
  selectedStatus,
  statusDisplayNames = {},
  statusListWithCounts = [],
  defaultTitle = "",
}) => {
  const getCount = (id) => {
    const item = statusListWithCounts?.find((s) => s.statusId === id);
    return Number(item?.count) || 0;
  };

  if (Array.isArray(selectedStatus)) {
    if (selectedStatus.length === 0) {
      return `${capitalizeEntireWord(defaultTitle) || defaultTitle} LIST (0)`;
    }
    return selectedStatus
      .map((id, index) => {
        const rawName = statusDisplayNames?.[id] || id;
        const name = capitalizeEntireWord(rawName) || rawName || "";
        const count = getCount(id);
        const isLast = index === selectedStatus.length - 1;
        return isLast ? `${name} LIST (${count})` : `${name} (${count})`;
      })
      .join(", ");
  }

  if (selectedStatus) {
    const rawName = statusDisplayNames?.[selectedStatus] || selectedStatus || defaultTitle;
    const name = capitalizeEntireWord(rawName) || rawName || "";
    const count = getCount(selectedStatus);
    return `${name} LIST (${count})`;
  }

  return `${capitalizeEntireWord(defaultTitle) || defaultTitle} LIST (0)`;
};

const Deals = ({
  dealCategoryId = DEFAULT_DEAL_CATEGORY_ID,
  dealCategoryName = DEFAULT_DEAL_CATEGORY_NAME,
}) => {
  const dispatch = useDispatch();

  const { dealView, loading, CountData } = useSelector((state) => state?.deals);
  const { statusList } = useSelector((state) => state?.enqMngt);
  const docLoading = useDocDownloadError();
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [columnControl, setColumnControl] = useState(null);
  const { allowedStatusIds } = useUserPrivileges(DEAL_STATUS_PRIVILEGE_MAP);

  useEffect(() => {
    const fetchStatus = async () => {
      const res = await dispatch(getStatusList("deals"));
      if (res.payload?.statusCode === 200) {
        const preferred = DEFAULT_DEAL_STATUSES.filter((id) =>
          allowedStatusIds.includes(id)
        );
        setSelectedStatus(
          preferred.length > 0
            ? preferred
            : allowedStatusIds.length > 0
              ? [allowedStatusIds[0]]
              : []
        );
      }
    };
    fetchStatus();
  }, [dispatch, allowedStatusIds]);

  useEffect(() => {
    if (selectedStatus !== null && selectedStatus !== undefined) {
      dispatch(dealsView({ selectedStatus, dealCategoryId }));
    }
  }, [dispatch, selectedStatus, dealCategoryId]);

  useEffect(() => {
    dispatch(count("DEALS"));
  }, [dispatch]);

  const { searchText, setSearchText, filteredData: searchFilteredData } =
    useSearchableTable(dealView);

  const filteredData = useMemo(() => {
    if (Array.isArray(selectedStatus) && selectedStatus.length === 0) return [];
    return searchFilteredData;
  }, [searchFilteredData, selectedStatus]);

  const handleStatusChange = useCallback((value) => {
    setSelectedStatus(value);
  }, []);

  const filteredStatusList = useMemo(() => {
    const augmentedStatusList = (statusList || []).slice();
    return augmentedStatusList.filter((s) => allowedStatusIds.includes(s.statusId));
  }, [statusList, allowedStatusIds]);

  const statusListWithCounts = useMemo(
    () => filteredStatusList.map((item) => {
      const isDeal = IS_DEAL_STATUS_IDS.includes(item.statusId);
      const matchedCount = CountData?.find(
        (c) => c.statusId === item.statusId && c.dealCategoryId === dealCategoryId,
      );
      return {
        ...item,
        count: isDeal ? Number(matchedCount?.count || 0) : undefined,
      };
    }),
    [filteredStatusList, CountData, dealCategoryId],
  );

  const handleDownload = useCallback(
    (type) => downloadPageHandle({
      dispatch,
      type,
      screenName: "deals",
      dealType: dealCategoryName,
      dealCategoryId,
      statusId: selectedStatus,
    }),
    [dispatch, selectedStatus, dealCategoryName, dealCategoryId],
  );

  const handleDownloadPdf = useCallback(() => handleDownload("pdf"), [handleDownload]);
  const handleDownloadExcel = useCallback(() => handleDownload("excel"), [handleDownload]);

  const tableTitle = useMemo(
    () =>
      (generateStatusTableTitle || buildStatusTableTitle)({
        selectedStatus,
        statusDisplayNames: DEAL_STATUS_LABEL_MAP,
        statusListWithCounts,
        defaultTitle: "Deals",
      }),
    [selectedStatus, statusListWithCounts],
  );

  return (
    <CustomCard>
      <TableHeader
        title={tableTitle}
        searchText={searchText}
        setSearchText={setSearchText}
        isDropdown={true}
        dropdownData={statusListWithCounts}
        selectedStatus={selectedStatus}
        handleStatusChange={handleStatusChange}
        statusDisplayNames={DEAL_STATUS_LABEL_MAP}
        isDownloadRequired={true}
        onDownloadPdf={handleDownloadPdf}
        onDownloadExcel={handleDownloadExcel}
        downloadLoading={docLoading}
        columnControl={columnControl}
      />

      <DealsTable
        tableData={filteredData}
        selectedStatus={selectedStatus}
        dealCategoryId={dealCategoryId}
        loading={loading}
        onColumnControlChange={setColumnControl}
      />
    </CustomCard>
  );
};

export default Deals;